import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adjustMarket } from "@/lib/economy-api";
import { parseZlatkaInput } from "@/lib/currency";
import { MARKET_ADJUST } from "@/constants/testIds";

// GM-only manual market shock: either a percentage move or an absolute new
// value, plus a mandatory in-world justification (shown in the company's
// history feed). Deliberately bypasses the automatic simulation's floor/
// ceiling - a GM reacting to an RP event has full narrative authority.
export const MarketAdjustDialog = ({ open, onClose, company, onAdjusted }) => {
  const [mode, setMode] = useState("pct"); // "pct" | "value"
  const [submitting, setSubmitting] = useState(false);

  if (!company) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const data = new FormData(e.target);
    const note = data.get("note")?.trim();
    if (!note) {
      toast("Chybí zdůvodnění", { description: "Zásah do trhu musí mít RP odůvodnění." });
      return;
    }

    let payload;
    if (mode === "pct") {
      const pct = Number(data.get("pct"));
      if (!Number.isFinite(pct)) {
        toast("Neplatná hodnota", { description: "Zadej procentní změnu, např. -40 pro pokles o 40 %." });
        return;
      }
      payload = { pct_change: pct / 100, note };
    } else {
      const newValue = parseZlatkaInput(data.get("value"));
      if (!newValue) {
        toast("Neplatná hodnota", { description: "Zadej novou hodnotu jako nezáporné číslo zlatek." });
        return;
      }
      payload = { new_value: newValue, note };
    }

    setSubmitting(true);
    try {
      await adjustMarket(company.id, payload);
      toast("Trh upraven", { description: `Hodnota podniku „${company.name}“ byla ručně upravena.` });
      onAdjusted();
    } catch (err) {
      toast("Úprava se nezdařila", { description: err.response?.data?.detail ?? "Zkus to prosím znovu." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-testid={MARKET_ADJUST.dialog}
        className="border-white/10 bg-[#17171b] text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            Zásah do trhu - {company.name}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Reaguje na RP událost ve světě. Na rozdíl od denní simulace může tenhle zásah
            prorazit podlahu i strop hodnoty.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("pct")}
              className={`flex-1 rounded-md border py-2 text-xs uppercase tracking-wide ${
                mode === "pct" ? "border-[#F0801F] text-[#F0801F]" : "border-white/20 text-white/60"
              }`}
            >
              Procentní změna
            </button>
            <button
              type="button"
              onClick={() => setMode("value")}
              className={`flex-1 rounded-md border py-2 text-xs uppercase tracking-wide ${
                mode === "value" ? "border-[#F0801F] text-[#F0801F]" : "border-white/20 text-white/60"
              }`}
            >
              Nová hodnota
            </button>
          </div>

          {mode === "pct" ? (
            <div>
              <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
                Změna (%), např. -40 pro pokles o 40 %
              </label>
              <input
                name="pct"
                type="number"
                step="0.1"
                required
                data-testid={MARKET_ADJUST.pctInput}
                className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
              />
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
                Nová hodnota (zlatky)
              </label>
              <input
                name="value"
                type="number"
                min="0"
                step="0.001"
                required
                data-testid={MARKET_ADJUST.valueInput}
                className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              RP zdůvodnění (povinné)
            </label>
            <textarea
              name="note"
              rows={3}
              required
              placeholder="např. Krčma vyhořela při požáru ve čtvrti Rowengardu…"
              data-testid={MARKET_ADJUST.noteInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid={MARKET_ADJUST.submitButton}
            className="w-full rounded-md bg-[#F0801F] py-3 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Upravuji…" : "Provést zásah"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
